import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { markUserAuthReady } from '@/utils/authReady';


// Get backend URL - always use current origin for same-domain API calls
const getBackendUrl = () => {
  // In production/deployed: use same origin (avoids cross-domain issues)
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  // Fallback for SSR/build
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL;
  }
  return '';
};

const API_URL = getBackendUrl();

// Backoff between /api/auth/me probe retries (iter-447 guards a valid
// session against a transiently-dropped cookie). Zeroed under test so the
// unit suite isn't gated by real timers.
const AUTH_PROBE_RETRY_MS =
  (typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'test')
    ? 0
    : 600;

// Iter-316 — bypass the platform's `response.clone()` telemetry wrapper
// to prevent "Response body is already used" errors during the AuthContext
// init probe. Same pattern used by authFetch / adminFetch.
const nativeFetch = (url, opts) => {
  const baseFetch =
    (typeof window !== 'undefined' && window.__corvanaNativeFetch) || fetch;
  return baseFetch(url, opts);
};

const AuthContext = createContext(null);

// Iter-316c — `token` is no longer exposed via AuthContext. The HttpOnly
// `access_token` cookie is the sole credential channel, and `isAuthenticated`
// is now derived from `!!user`. All previous `useAuth().token` consumers
// have been migrated to either drop the destructure (dead code) or read
// `user` (which is the source-of-truth signal that `/api/auth/me`
// returned 200). Nothing reads the JWT from JavaScript anymore.
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is authenticated on mount
  useEffect(() => {
    const initAuth = async () => {
      // The Tech Panel (/tech-admin) runs its OWN admin-session auth and
      // has no user-app session — probing /api/auth/me there only produces
      // first-paint 401 console noise. Skip the user probe entirely.
      // Same for /login and /register: there is no session yet, so probing
      // just floods the console with 401 /me + /refresh pairs. A successful
      // login() sets `user` directly from its response, so nothing is lost.
      const skipProbe = typeof window !== 'undefined' && (
        window.location.pathname.startsWith('/tech-admin') ||
        window.location.pathname.startsWith('/login') ||
        window.location.pathname.startsWith('/register')
      );
      if (skipProbe) {
        setLoading(false);
        markUserAuthReady();
        return;
      }

      // Anonymous visitors on public/marketing pages have no session to find —
      // probing /api/auth/me (+ /refresh) there just produces first-paint 401
      // console noise (flagged by Lighthouse Best-Practices) and two needless
      // requests on every marketing-page load. Every real login/OAuth flow
      // writes the `corvana_user` snapshot AND lands under an authenticated area
      // (/app, /corvana-crm), so gating on both preserves session restoration
      // everywhere it matters while skipping the probe for logged-out visitors.
      if (typeof window !== 'undefined') {
        const hasSessionHint = !!localStorage.getItem('corvana_user');
        const inAuthedArea = /^\/(app|corvana-crm|tech-admin)(\/|$)/.test(window.location.pathname);
        if (!hasSessionHint && !inAuthedArea) {
          setLoading(false);
          markUserAuthReady();
          return;
        }
      }

      {
        // Always probe /api/auth/me with credentials:'include' — the
        // HttpOnly access_token cookie is the only credential channel.
        // We can't read the cookie value from JS, so we just call /me
        // and treat a 200 as "session valid". Iter-447 — the preview
        // harness intermittently drops the cookie / aborts the request
        // on a fast navigation, surfacing as a transient 401 or
        // "Failed to fetch". We retry the probe a few times before
        // settling on the unauthenticated state so a valid session is
        // never silently discarded (which previously hid gated UI).
        const probeMe = async () => {
          const response = await nativeFetch(`${API_URL}/api/auth/me`, {
            credentials: 'include',
          });
          if (response.ok) {
            const responseText = await response.text();
            setUser(JSON.parse(responseText));
            return true;
          }
          if (response.status === 401) {
            // Try a refresh before bailing — the refresh path re-pulls /me.
            return await refreshToken();
          }
          return false;
        };

        let ok = false;
        for (let attempt = 0; attempt < 3 && !ok; attempt += 1) {
          try {
            ok = await probeMe();
          } catch (err) {
            // Transient network/abort error — fall through to retry.
            if (attempt === 2) console.error('Auth init error:', err);
          }
          // Iter-447 — the preview harness can transiently drop the cookie
          // and surface a 401 even for a VALID session, so we retry a few
          // times before settling on "logged out" rather than discarding a
          // good session. (The tech-panel skip above already removes the
          // bulk of unauthenticated-probe noise.)
          if (!ok && attempt < 2) {
            await new Promise((resolve) => setTimeout(resolve, AUTH_PROBE_RETRY_MS));
          }
        }
      }
      setLoading(false);
      markUserAuthReady();
    };

    initAuth();
  // eslint-disable-next-line
  }, []);

  const login = async (email, password) => {
    setError(null);
    try {
      const response = await nativeFetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include', // For httpOnly access/refresh cookies
        body: JSON.stringify({ email, password })
      });

      // Use text() then JSON.parse() to prevent any clone/double-read issues
      const responseText = await response.text();
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        throw new Error('Invalid server response');
      }

      if (!response.ok) {
        throw new Error(data.detail || 'Invalid email or password');
      }

      // Account has MFA enabled — caller must complete a step-up before a
      // session cookie is issued. Surface the available methods.
      if (data.requires_2fa) {
        return { success: false, requires2fa: true, methods: data.available_methods || ['totp'], user: data.user };
      }

      // Backend has set HttpOnly access_token + refresh_token cookies + a
      // non-HttpOnly csrf_token cookie. Iter-316c — we only persist the
      // non-sensitive `corvana_user` snapshot for offline hydration.
      // The JWT never touches localStorage OR React state.
      localStorage.setItem('corvana_user', JSON.stringify(data.user));
      setUser(data.user);

      // Trigger storage event for BusinessContext to pick up
      window.dispatchEvent(new Event('storage'));

      // Surface the mandatory-rollout enrollment prompt (null when enrolled).
      if (data.mfa_enrollment) {
        try { localStorage.setItem('corvana_mfa_enrollment', JSON.stringify(data.mfa_enrollment)); } catch (_) {}
      } else {
        localStorage.removeItem('corvana_mfa_enrollment');
      }

      return { success: true, user: data.user, mfaEnrollment: data.mfa_enrollment || null };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Complete a 2FA step-up (authenticator / email / recovery code). On success
  // the backend sets the session cookies and returns the full user record.
  const verifyTwoFactor = async (email, password, code, method = 'totp') => {
    try {
      const response = await nativeFetch(`${API_URL}/api/auth/2fa/verify-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password, code, method }),
      });
      const text = await response.text();
      let data;
      try { data = JSON.parse(text); } catch { throw new Error('Invalid server response'); }
      if (!response.ok) throw new Error(data.detail || 'Invalid verification code');
      localStorage.setItem('corvana_user', JSON.stringify(data.user));
      localStorage.removeItem('corvana_mfa_enrollment');
      setUser(data.user);
      window.dispatchEvent(new Event('storage'));
      return { success: true, user: data.user };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  // Request an email one-time code during login step-up.
  const sendEmailLoginCode = async (email, password) => {
    try {
      const response = await nativeFetch(`${API_URL}/api/auth/2fa/email/send-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || 'Could not send code');
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  // Complete a passkey login (Face ID / fingerprint). Returns the user on success.
  const loginWithPasskey = async (email) => {
    try {
      const { startAuthentication } = await import('@simplewebauthn/browser');
      const optResp = await nativeFetch(`${API_URL}/api/auth/passkeys/login/options`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email }),
      });
      if (!optResp.ok) {
        const e = await optResp.json().catch(() => ({}));
        throw new Error(e.detail || 'No passkey found for this account');
      }
      const options = await optResp.json();
      const assertion = await startAuthentication({ optionsJSON: options });
      const verifyResp = await nativeFetch(`${API_URL}/api/auth/passkeys/login/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ...assertion, state: options.state }),
      });
      const text = await verifyResp.text();
      let data;
      try { data = JSON.parse(text); } catch { throw new Error('Invalid server response'); }
      if (!verifyResp.ok) throw new Error(data.detail || 'Passkey sign-in failed');
      localStorage.setItem('corvana_user', JSON.stringify(data.user));
      localStorage.removeItem('corvana_mfa_enrollment');
      setUser(data.user);
      window.dispatchEvent(new Event('storage'));
      return { success: true, user: data.user };
    } catch (err) {
      const msg = err.name === 'NotAllowedError' ? 'Passkey prompt cancelled or timed out.' : err.message;
      return { success: false, error: msg };
    }
  };

  const register = async (email, firstName, lastName, password) => {
    setError(null);
    try {
      const response = await nativeFetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email,
          first_name: firstName,
          last_name: lastName,
          password
        })
      });

      // Use text() then JSON.parse() to prevent any clone/double-read issues
      const responseText = await response.text();
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        throw new Error('Invalid server response');
      }

      if (!response.ok) {
        if (response.status === 409) {
          throw new Error('An account with this email already exists');
        }
        throw new Error(data.detail || 'Registration failed');
      }
      
      // Auto-login after registration
      return await login(email, password);
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  const refreshToken = async () => {
    try {
      const response = await nativeFetch(`${API_URL}/api/auth/refresh`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        // Iter-316c — cookie refreshed by the backend; we only need to
        // re-pull /me to sync the user record.
        const userResponse = await nativeFetch(`${API_URL}/api/auth/me`, {
          credentials: 'include',
        });
        
        if (userResponse.ok) {
          const userText = await userResponse.text();
          let userData;
          try {
            userData = JSON.parse(userText);
          } catch {
            return false;
          }
          setUser(userData);
          return true;
        }
      }
      return false;
    } catch (err) {
      console.error('Token refresh failed:', err);
      return false;
    }
  };

  const logout = useCallback(async () => {
    try {
      // Extract csrf_token cookie so the CSRF middleware accepts the request.
      const csrfMatch = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/);
      const csrfToken = csrfMatch ? decodeURIComponent(csrfMatch[1]) : null;
      await nativeFetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: csrfToken ? { 'X-CSRF-Token': csrfToken } : {}
      });
    } catch (err) {
      console.error('Logout error:', err);
    }

    // Iter-316c — proactively evict any pre-migration tokens that may
    // still be sitting in localStorage from older builds. Nothing else
    // reads them.
    localStorage.removeItem('corvana_access_token');
    localStorage.removeItem('corvana_user');
    localStorage.removeItem('corvana_customer_session'); // legacy session
    setUser(null);
    
    // Trigger storage event for BusinessContext
    window.dispatchEvent(new Event('storage'));
  }, []);

  // Iter-316c — `isAuthenticated` is now derived purely from `!!user`.
  // The cookie is the credential; if `/api/auth/me` succeeded, `user`
  // is populated and the session is valid.
  const isAuthenticated = !!user;
  
  const hasRole = useCallback((role) => {
    // Check roles array first
    if (user?.roles?.includes(role)) {
      return true;
    }
    
    // Check single role field
    const userRole = user?.role;
    if (userRole === role) {
      return true;
    }
    
    // Map super_admin to owner-level access
    if (userRole === 'super_admin') {
      // super_admin has all permissions
      return ['owner', 'management', 'sales', 'support', 'admin'].includes(role);
    }
    
    return false;
  }, [user]);

  const isOwner = useCallback(() => {
    return hasRole('owner') || user?.role === 'super_admin';
  }, [hasRole, user]);

  const value = {
    user,
    loading,
    error,
    isAuthenticated,
    login,
    verifyTwoFactor,
    sendEmailLoginCode,
    loginWithPasskey,
    register,
    logout,
    refreshToken,
    hasRole,
    isOwner
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
